import React from 'react';
import moment from 'moment';

import { LRS_OBJECT_TYPES, LRS_ACTIONS } from '@techchange/components';
import { messages } from '../i18n';

const getIntlAnchor = ({ href, chunks }) => (
	<a
		href={href}
		rel="noreferrer noopener"
		target="_blank">
		{chunks}
	</a>
);

/**
 * Traverses the rule tree and populates a table with the progress for each resource rule.
 * Progress is defined as the percentage of completed subconditions in the subtree.
 *
 * @param {object}   rule          The root rule of the subtree.
 * @param {object}   rulesHash     Look-up table for rule entities by object id.
 * @param {object}   trackersHash  Look-up table for user tracker entities by rule, condition,
 *                                 and subcondition id.
 * @param {object}   progresses    Look-up table for progresses by rule id.
 *
 * @returns {object} Object containing the number of completed leaves and total leaves
 *	                 for the subtree.
 */
export function calculateRuleProgresses(rule, rulesHash, trackersHash, progresses) {

	let completeLeaves = 0;

	let totalLeaves = 0;

	// Check if this rule has a condition
	const condition = rule.condition;

	if (condition && condition.active && condition.subconditions) {

		// Filter out inactive subconditions
		const activeSubconditions = condition.subconditions.filter((sub) => sub.active);

		// Loop through all the subconditions and find the percentage of
		//	completed leaves in the subtree
		for (let i = 0; i < activeSubconditions.length; i++) {

			const subcondition = activeSubconditions[i];

			// Check if this subcondition has a nested child rule to check
			if (subcondition.action === 'completed' && subcondition.object_id !== rule.object_id) {

				const subRule = rulesHash[subcondition.object_id];

				if (subRule && subRule.active && subRule.id !== rule.id) {

					// Continue down the tree
					const subTreeData = calculateRuleProgresses(subRule, rulesHash, trackersHash, progresses);

					completeLeaves += subTreeData.completeLeaves;

					totalLeaves += subTreeData.totalLeaves;

				}

			// Otherwise, we've reached a leaf subcondition in the tree
			} else {

				totalLeaves += 1;

				const tracker =
					trackersHash[`${subcondition.rule_id}:${subcondition.condition_id}:${subcondition.id}`];

				const status = tracker && tracker.status;

				if (status === 'complete') {

					completeLeaves += 1;

				}

			}

		}

		// Save the progress for this subtree
		progresses[rule.id] = (totalLeaves === 0) ? totalLeaves : (completeLeaves / totalLeaves * 100);

		return { completeLeaves, totalLeaves };

	}

	// We should never get here, but just in case there is no condition and subconditions
	return { completeLeaves, totalLeaves };

}

/**
 * Traverses the completion requirements (i.e. rule tree) and finds the number of
 * subcondition leaves that can be completed as of the current time (i.e. are open),
 * as well as the total number of subcondition leaves.
 *
 * @param {object} nodeRule         The current rule to calculate.
 * @param {object} rulesByObjectID  Look-up table for rules keyed by object_id.
 * @param {object} resourcesByID    Look-up table for resources keyed by id.
 * @param {object} currentTime      The current time.
 * @param {boolean} isParentClosed  Specifies whether or not the parent rule is closed.
 *
 * @returns {object} result  The number of leaves that can be completed as of the
 *                           current time (i.e. are open) and the
 *                           total number of subcondition leaves.
 */
function getNumOpenRuleLeaves(
	nodeRule,
	rulesByObjectID,
	resourcesByID,
	currentTime,
	isParentClosed = false
) {

	let openLeaves = 0, totalLeaves = 0;

	// Check if the current rule is required
	if (nodeRule && nodeRule.active) {

		// Find the resource (if any) that is associated with the current rule
		const resource = resourcesByID[nodeRule.object_id];

		// Check if the current rule is closed, i.e. not open yet
		const isClosed =
			isParentClosed ||
			(resource && resource.open_date && moment(resource.open_date).isAfter(currentTime));

		// Check if this rule has an active condition
		if (nodeRule.condition && nodeRule.condition.active && nodeRule.condition.subconditions) {

			// Filter out inactive subconditions
			const activeSubconditions =
				nodeRule.condition.subconditions.filter((sub) => sub && sub.active);

			// Loop through all the subconditions and check if the nested rules are open/closed
			for (let i = 0, len = activeSubconditions.length; i < len; i++) {

				const subcondition = activeSubconditions[i];

				// Find the nested rule (if any) for the current subcondition
				const childRule = rulesByObjectID[subcondition.object_id];

				// If the current subcondition has a nested rule, we'll need to continue recursively
				if (childRule && childRule.id !== nodeRule.id) {

					const subTreeData = getNumOpenRuleLeaves(
						childRule,
						rulesByObjectID,
						resourcesByID,
						currentTime,
						isClosed,
					);

					openLeaves += subTreeData.openLeaves;

					totalLeaves += subTreeData.totalLeaves;

				// Otherwise, we've reached a leaf subcondition in the completion requirements rule tree
				} else {

					totalLeaves += 1;

					if (!isClosed) {

						openLeaves += 1;

					}

				}

			}

		}

	}

	return { openLeaves, totalLeaves };

}

/**
 * Calculates the percentage of subcondition leaves associated with
 * content that can be completed as of the current time.
 *
 * @param {object} rootRule         The root rule for the scope.
 * @param {object} rulesByObjectID  Look-up table for rules keyed by object_id.
 * @param {object} resourcesByID    Look-up table for resources keyed by id.
 * @param {object} currentTime      The current time.
 *
 * @returns {integer} result  The percentage of subcondition leaves associated with with
 *                            content that can be completed as of the current time.
 */
export function calculatePctOpenRuleLeaves(rootRule, rulesByObjectID, resourcesByID, currentTime) {

	const { openLeaves, totalLeaves } = getNumOpenRuleLeaves(
		rootRule,
		rulesByObjectID,
		resourcesByID,
		currentTime,
	);

	return totalLeaves === 0 ? 0 : (openLeaves / totalLeaves * 100);

}

/**
 * Determines the required subconditions given a completion tracking rule.
 *
 * @param {Object} rule           The rule.
 * @param {Object} resourcesByID  A map of the resources for the scope hashed by ID.
 *
 * @returns {Array}            An array containing the required subconditions
 *                             sorted by resource weight.
 */
export function getCompletionRequirements(rule, resourcesByID) {

	let requiredSubconditions;

	if (rule &&
		rule.active &&
		rule.condition &&
		rule.condition.active && rule.condition.subconditions) {

		// Filter out any inactive subconditions
		requiredSubconditions =
			rule.condition.subconditions.filter((sub) => sub.active);

		// Sort the subconditions so that the resource order is maintained
		requiredSubconditions.sort((sub1, sub2) => {

			const child1 = resourcesByID[sub1.object_id];
			const child2 = resourcesByID[sub2.object_id];

			if (!child1 || !child2) {
				return 0;
			}

			return child1.weight - child2.weight;

		});

	}

	return requiredSubconditions;

}

/**
 * Gets a mapping of LRS object types to meta-data used for rendering.
 *
 * Meta-data Keys:
 *
 * {Object} actions - The set of actions for this activity object. Each key represents
 *	an action for the object.
 * {Object} actions[<action_name>].display - An internationalized message that describes
 *	the action on the object.
 * {Function} actions[<action_name>].parentDisplay - A function that returns an
 *	internationalized message describing the action's relation to its parent object.
 *
 * {Function} getObjectLink - Function that formats the the object URL.
 *	Parameters must include the LRS statement.
 *	If the link is for the parent object, use the isParent flag.
 *	If additional data is needed, use the options object.
 *
 * @param {Object} intl  The provider for internationalization.
 *
 * @returns {Object}        The LRS activity action map.
 */
export function getCompletionRequirementsLRSActionMap(intl) {

	return {

		/**
		 * DISCUSSION COMMENT
		 */
		[LRS_OBJECT_TYPES.COMMENT]: {
			actions: {
				// "Post a response (in the thread {{thread_title}}"
				[LRS_ACTIONS.CREATED]: {
					display: () =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_DISCUSSION_COMMENT_CREATED),
					parentDisplay: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_DISCUSSION_COMMENT_CREATED_PARENT, {
							thread_title: subcondition.parent_object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.parent_object_link,
								chunks
							}),
						}),
				},
			},
		},

		/**
		 * DISCUSSION THREAD
		 */
		[LRS_OBJECT_TYPES.THREAD]: {
			actions: {
				// "Start a thread in {topic title}"
				[LRS_ACTIONS.CREATED]: {
					display: () =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_DISCUSSION_THREAD_CREATED),
					parentDisplay: (subcondition) => (
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_DISCUSSION_THREAD_CREATED_PARENT, {
							topic_title: subcondition.parent_object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.parent_object_link,
								chunks
							}),
						})
					),
				}
			},
			getObjectLink: (subcondition, isParent) => {

				const thread = isParent ? subcondition.parent_object : subcondition.object;
				const threadSlug = thread && thread.slug;

				if (threadSlug) {
					return `/discussion/threads/${threadSlug}`;
				}

				return '/discussion';

			},
		},

		/**
		 * DISCUSSION TOPIC
		 */
		[LRS_OBJECT_TYPES.TOPIC]: {
			actions: {},
			getObjectLink: (subcondition, isParent) => {

				const topic = isParent ? subcondition.parent_object : subcondition.object;
				const topicSlug = topic && topic.slug;

				if (topicSlug) {
					return `/discussion/topics/${topicSlug}`;
				}

				return '/discussion/topics';

			},
		},

		/**
		 * EMBED
		 */
		[LRS_OBJECT_TYPES.EMBED]: {
			actions: {
				// "Complete {embed title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {embed title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * TC AND GRAVITY FORMS
		 */
		[LRS_OBJECT_TYPES.FORM]: {
			actions: {
				// "Complete {form title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {form title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * FORM ENTRY
		 */
		[LRS_OBJECT_TYPES.FORM_ENTRY]: {
			actions: {
				// "Submit a response to {form title}"
				[LRS_ACTIONS.CREATED]: {
					display: () =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_FORM_ENTRY_CREATED),
					parentDisplay: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_FORM_ENTRY_CREATED_PARENT, {
							form_title: subcondition.parent_object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.parent_object_link,
								chunks
							}),
						}),
				},
			},
		},

		/**
		 * IMAGE
		 */
		[LRS_OBJECT_TYPES.IMAGE]: {
			actions: {
				// "Click {image title}"
				[LRS_ACTIONS.CLICKED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_CLICKED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Complete {image title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {image title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * LINK
		 */
		[LRS_OBJECT_TYPES.LINK]: {
			actions: {
				// "Click {link title}"
				[LRS_ACTIONS.CLICKED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_CLICKED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								// href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * MODULE
		 */
		[LRS_OBJECT_TYPES.MODULE]: {
			actions: {
				// "Complete {module_title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * PAGE
		 */
		[LRS_OBJECT_TYPES.PAGE]: {
			actions: {
				// "Complete {page_title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * POINT
		 */
		[LRS_OBJECT_TYPES.POINT]: {
			actions: {
				// "Earn {point total} points"
				[LRS_ACTIONS.EARNED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_POINT_EARNED, {
							point_total: subcondition.meta && subcondition.meta.threshold,
						}),
				},
			},
		},

		/**
		 * POLL
		 */
		[LRS_OBJECT_TYPES.POLL]: {
			actions: {
				// "Complete {poll title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {poll title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * QUIZ
		 */
		[LRS_OBJECT_TYPES.QUIZ]: {
			actions: {
				// "Complete {quiz title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Pass {quiz title}"
				[LRS_ACTIONS.PASSED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_QUIZ_PASSED, {
							quiz_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {quiz title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * SESSION
		 */
		[LRS_OBJECT_TYPES.SESSION]: {
			actions: {
				// "Complete the session"
				[LRS_ACTIONS.COMPLETED]: {
					display: () =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_SESSION_COMPLETED),
				},
			},
			linkToResource: true,
		},

		/**
		 * SLIDE
		 */
		[LRS_OBJECT_TYPES.SLIDE]: {
			actions: {
				// "Complete {slide_title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {slide_title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "View {slide_title}"
				[LRS_ACTIONS.VIEWED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_SLIDE_VIEWED, {
							slide_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * SUBMODULE
		 */
		[LRS_OBJECT_TYPES.SUBMODULE]: {
			actions: {
				// "Complete {submodule_title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

		/**
		 * VIDEO
		 */
		[LRS_OBJECT_TYPES.VIDEO]: {
			actions: {
				// "Complete {video_title}"
				[LRS_ACTIONS.COMPLETED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_COMPLETED, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Finish {video_title}"
				[LRS_ACTIONS.FINISHED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_VIDEO_FINISHED, {
							video_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Mark {video_title} as complete"
				[LRS_ACTIONS.MARKED_COMPLETE]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_OBJECT_MARKED_COMPLETE, {
							object_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Pause {video_title}"
				[LRS_ACTIONS.PAUSED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_VIDEO_PAUSED, {
							video_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
				// "Play {video_title}"
				[LRS_ACTIONS.PLAYED]: {
					display: (subcondition) =>
						intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_REQUIREMENT_DESCRIPTION_VIDEO_PLAYED, {
							video_title: subcondition.object_title,
							anchor: (chunks) => getIntlAnchor({
								href: subcondition.object_link,
								chunks
							}),
						}),
				},
			},
			linkToResource: true,
		},

	};

}

/**
 * Gets a mapping of subcondition object types to meta-data used for rendering.
 *
 * @returns {object}    The LRS rule action map.
 */
export const getLRSActionMapRule = () => {
	return {
		embed: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
			},
		},
		event: {
			actions: {
				attended: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_ATTEND,
				},
				RSVPed: {
					display: messages.TC_COMPONENTS_UTILS_LRS_REQUIREMENTS_RSVP_EVENT_TEXT,
				},
			},
			referenceKey: 'object_id',
			getObjectLink: (ref) => `/events/${ref}`,
		},
		form: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
			},
		},
		'form-entry': {
			display: messages.TC_COMPONENTS_UTILS_LRS_REQUIREMENTS_FORM_ENTRY_TEXT,
			actions: {
				created: {
					display: messages.TC_COMPONENTS_UTILS_LRS_REQUIREMENTS_FORM_ENTRY_CREATE_TEXT,
					parentDisplay: messages.TC_COMPONENTS_UTILS_LRS_REQUIREMENTS_SUBCONDITION_PARENT_TEXT,
				},
			},
		},
		image: {
			actions: {
				clicked: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_CLICK,
				},
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
			},
		},
		link: {
			actions: {
				clicked: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_CLICK,
				},
			},
		},
		module: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				viewed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_VIEW,
				},
			},
		},
		poll: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
			},
		},
		quiz: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
				passed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_PASS,
				},
			},
		},
		slide: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
				viewed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_VIEW,
				},
			},
		},
		submodule: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
			},
		},
		miro: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				}
			},
		},
		video: {
			actions: {
				completed: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_COMPLETE,
				},
				played: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_PLAY,
				},
				paused: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_PAUSE,
				},
				finished: {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_FINISH,
				},
				'marked-complete': {
					display: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_ACTION_SELF_COMPLETE,
				},
			},
		},
	};
};
